/**
 * Which form configures which field type.
 *
 * Exhaustive over `FieldTypeName` with `null` a real answer, the same shape as
 * the cell editors: a type with nothing to configure and a type this build
 * cannot yet configure both have no form, and `FIELD_DEFAULTS` is where the two
 * are told apart. A twenty-eighth field type fails to compile here until it has
 * been answered for, rather than opening a dialog with an empty middle.
 */
import type { ReactNode } from 'react'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import type { Field, FieldPropertiesOf, FieldTypeName } from '@/api/types'
import { Toggle } from './controls'
import {
  DateFormatting,
  DurationFormatting,
  NumberDefault,
  NumberFormatting,
  changing,
  type DateFormattingProperties,
  type DurationFormattingProperties,
  type NumberFormattingProperties,
} from './formatting'
import { FormViewEditRowForm } from './edit-row'
import { FormulaForm } from './formula'
import { CountForm, LinkRowForm, LookupForm, RollupForm } from './relations'
import { MultipleSelectForm, SingleSelectForm } from './select'
import {
  BooleanForm,
  CollaboratorsForm,
  LongTextForm,
  PasswordForm,
  RatingForm,
  TextForm,
} from './simple'
import type { FieldProperties } from '../../config'
import type { SubForm } from './types'

/** The three types that hold a moment in time and share their five properties. */
const DATE_LIKE: ReadonlySet<FieldTypeName> = new Set([
  'date',
  'last_modified',
  'created_on',
])

const DateFieldForm: SubForm<'date'> = ({ properties, onChange, field }) => (
  <DateFormatting
    properties={properties}
    onChange={changing<DateFormattingProperties, Partial<FieldPropertiesOf<'date'>>>(
      onChange,
    )}
    // What the field's values are read in today, so that pinning a different
    // zone can offer to move them with it. Undefined -- no offer -- when there
    // is nothing of the kind to move: a field only now becoming a date holds no
    // moments yet, and the ones a created-on holds did happen when they say.
    convertingFrom={
      field.read_only || !DATE_LIKE.has(field.type)
        ? undefined
        : ((field as { date_force_timezone?: string | null }).date_force_timezone ?? null)
    }
  />
)

const NumberFieldForm: SubForm<'number'> = ({ properties, onChange }) => {
  const { t } = useTranslation()
  const negative = useId()
  const stored = properties.number_default ?? null

  return (
    <>
      <NumberFormatting
        properties={properties}
        onChange={changing<
          NumberFormattingProperties,
          Partial<FieldPropertiesOf<'number'>>
        >(onChange)}
      />
      <Toggle
        id={negative}
        label={t('fieldConfig.allowNegative')}
        checked={properties.number_negative === true}
        onChange={(number_negative) => {
          // A default the field would no longer accept goes with the setting
          // that allowed it. Upstream substitutes zero, which is a value nobody
          // asked for and one that every new row would then carry.
          const impossible =
            !number_negative && stored !== null && stored.trimStart().startsWith('-')
          onChange({
            number_negative,
            ...(impossible ? { number_default: null } : {}),
          })
        }}
      />
      <NumberDefault
        properties={properties}
        stored={stored}
        onChange={(number_default) => {
          onChange({ number_default })
        }}
      />
    </>
  )
}

const DurationFieldForm: SubForm<'duration'> = ({ properties, onChange }) => (
  <DurationFormatting
    properties={properties}
    onChange={changing<
      DurationFormattingProperties,
      Partial<FieldPropertiesOf<'duration'>>
    >(onChange)}
  />
)

/**
 * The form for each field type, or nothing.
 *
 * Nothing means one of two things, and `FIELD_DEFAULTS` says which: a type with
 * no properties of its own -- an email field is an email field -- or one whose
 * configuration this build cannot yet produce, which is also why the type picker
 * does not offer it.
 */
export const FIELD_FORMS: { [T in FieldTypeName]: SubForm<T> | null } = {
  text: TextForm,
  long_text: LongTextForm,
  url: null,
  email: null,
  number: NumberFieldForm,
  rating: RatingForm,
  boolean: BooleanForm,
  date: DateFieldForm,
  last_modified: DateFieldForm,
  created_on: DateFieldForm,
  last_modified_by: null,
  created_by: null,
  duration: DurationFieldForm,
  link_row: LinkRowForm,
  file: null,
  single_select: SingleSelectForm,
  multiple_select: MultipleSelectForm,
  phone_number: null,
  formula: FormulaForm,
  count: CountForm,
  rollup: RollupForm,
  lookup: LookupForm,
  multiple_collaborators: CollaboratorsForm,
  uuid: null,
  autonumber: null,
  password: PasswordForm,
  form_view_edit_row: FormViewEditRowForm,
}

/** What a form looks like once the type it belongs to is only known at runtime. */
type AnySubForm = (props: {
  properties: FieldProperties
  onChange: (changes: FieldProperties) => void
  field: Field
  tableId: number
  name: string
}) => ReactNode

/**
 * The configuration form for whatever type the draft currently names.
 *
 * The one place the twenty-seven forms are collapsed into one, and therefore the
 * one cast: the map is keyed by field type and the draft's properties are keyed
 * by string, and nothing joins the two back up for a type only known while the
 * program is running. Everything on either side of this line is checked.
 */
export function FieldConfigForm({
  type,
  ...props
}: {
  type: FieldTypeName
  properties: FieldProperties
  onChange: (changes: FieldProperties) => void
  field: Field
  tableId: number
  name: string
}) {
  const Form = FIELD_FORMS[type] as AnySubForm | null
  return Form === null ? null : <Form {...props} />
}
